import { useEffect, useState } from 'react';
import { IdcardOutlined, LockOutlined, MobileOutlined, SafetyCertificateOutlined, SafetyOutlined, TeamOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input } from 'antd';
import { requestSignInCode, setToken, signIn } from '../api';
function Brand({ dark }) {
    return (<div className="brand" style={{ padding: 0, height: 'auto', color: dark ? '#fff' : 'var(--ink)' }}>
      <span className="brand-mark" style={{ color: '#fff' }}>
        K
      </span>
      <span className="brand-name">
        Kivonex
        <small>Admin console</small>
      </span>
    </div>);
}
/** Admin sign-in: the mobile number, then the one-time code sent to it. There is no password. */
export function LoginPage({ onSignedIn }) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [mobile, setMobile] = useState('');
    const [sentTo, setSentTo] = useState(null);
    const [wait, setWait] = useState(0);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState(false);
    // A new set of empty code boxes (cursor in the first) after a wrong code.
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (wait <= 0)
            return;
        const timer = window.setTimeout(() => setWait((seconds) => seconds - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [wait]);
    async function sendCode(number) {
        setSubmitting(true);
        setError(null);
        try {
            const result = await requestSignInCode(number);
            setMobile(number);
            setSentTo(result.mobile);
            setWait(result.resendAfterSeconds);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Could not send the code');
        }
        finally {
            setSubmitting(false);
        }
    }
    async function verify(code) {
        if (submitting)
            return;
        if (!/^\d{6}$/.test(code))
            return setCodeError(true);
        setSubmitting(true);
        setError(null);
        try {
            const result = await signIn(mobile, code);
            setToken(result.token);
            onSignedIn(result.token);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Sign-in failed');
            setCode('');
            setAttempt((count) => count + 1);
        }
        finally {
            setSubmitting(false);
        }
    }
    function reset() {
        setSentTo(null);
        setError(null);
        setWait(0);
    }
    return (<div className="login">
      <aside className="login-art">
        <Brand dark/>
        <div>
          <span className="login-eyebrow">
            <SafetyCertificateOutlined /> Kivonex Cloud
          </span>
          <h2>Every customer, plan and device in one place.</h2>
          <p>Set the plans customers buy, follow their payments and keep an eye on cloud sync for every Kivonex Accounts customer.</p>
          <ul className="login-points">
            <li>
              <span className="login-point-icon">
                <TeamOutlined />
              </span>
              <span>
                <b>Accounts and devices</b>
                Customers sign in with their mobile number; see the computers they use.
              </span>
            </li>
            <li>
              <span className="login-point-icon">
                <IdcardOutlined />
              </span>
              <span>
                <b>Plans and payments</b>
                Price plans, read paid invoices and issue a plan by hand when needed.
              </span>
            </li>
            <li>
              <span className="login-point-icon">
                <SafetyOutlined />
              </span>
              <span>
                <b>Audited by default</b>
                Every change, sign-in and payment is written to the audit log.
              </span>
            </li>
          </ul>
        </div>
        <div className="login-foot">
          <LockOutlined /> Company books stay on each customer's computer unless they sync them.
        </div>
      </aside>

      <main className="login-form">
        <div className="login-card">
          <div className="login-mobile-brand">
            <Brand />
          </div>
          <div className="login-steps" aria-hidden>
            <span className="on"/>
            <span className={sentTo ? 'on' : ''}/>
          </div>
          <span className="login-step-icon">{sentTo ? <SafetyOutlined /> : <MobileOutlined />}</span>
          <h1>{sentTo ? 'Enter your code' : 'Sign in'}</h1>
          {sentTo ? (<p>
              We sent a 6-digit code to <strong>{sentTo}</strong>. It works once, for 10 minutes.
            </p>) : (<p>Enter the mobile number of your admin user. We'll send a one-time code to it.</p>)}
          {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 20 }}/>}
          {!sentTo ? (<Form layout="vertical" requiredMark={false} size="large" onFinish={(values) => void sendCode(values.mobile)}>
              <Form.Item label="Mobile number" name="mobile" rules={[{ required: true, pattern: /^(\+?91[\s-]*)?[6-9](\s*-?\d){9}$/, message: 'Enter a 10-digit mobile number.' }]}>
                <Input prefix={<span className="login-prefix">+91</span>} placeholder="98765 43210" inputMode="tel" autoFocus autoComplete="tel-national" maxLength={16}/>
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={submitting}>
                Send code
              </Button>
            </Form>) : (<form onSubmit={(event) => {
                event.preventDefault();
                void verify(code);
            }}>
              <div className="login-otp">
                <Input.OTP key={attempt} length={6} size="large" autoFocus inputMode="numeric" aria-label="Sign-in code" disabled={submitting} status={codeError ? 'error' : undefined} value={code} onInput={(cells) => {
                setCode(cells.join(''));
                setCodeError(false);
            }} 
        // Six digits sign in by themselves.
        onChange={(value) => void verify(value)}/>
                {codeError && <div className="login-otp-error">Enter the 6-digit code.</div>}
              </div>
              <Button type="primary" size="large" htmlType="submit" block loading={submitting}>
                Verify and sign in
              </Button>
              <div className="login-code-actions">
                <Button type="link" onClick={() => reset()} style={{ padding: 0 }}>
                  Change number
                </Button>
                <Button type="link" disabled={wait > 0 || submitting} onClick={() => void sendCode(mobile)} style={{ padding: 0 }}>
                  {wait > 0 ? `Resend in ${wait}s` : 'Resend code'}
                </Button>
              </div>
            </form>)}
          <div className="login-secure">
            <LockOutlined /> No password. Each code works once.
          </div>
        </div>
      </main>
    </div>);
}
